import { Component } from '@theme/component';

/**
 * Pinterest intent-event relay.
 *
 * Relays selected Nouvel Heritage intent-funnel events (published by
 * high-intent-visit-tracking.js via the `nh:intent-event` CustomEvent and
 * the `window.__nhIntentEventBuffer` catch-up buffer) to Pinterest as
 * custom events via `pintrk('track', '<event-name>')`.
 *
 * Runs in the MAIN window on purpose — the Pinterest for Shopify app's
 * pixel lives in the sandboxed Customer Events iframe and cannot expose
 * `pintrk` here, and a main-window tag can read the first-party Pinterest
 * cookies (`_epik` click ID, `_pin_unauth`) that sandboxed pixels cannot,
 * which materially improves match quality. The Pinterest core script is
 * loaded lazily, only when the first relayable event actually fires, so
 * pages where no intent threshold is crossed load nothing.
 *
 * No standard events (pagevisit, checkout, …) are ever sent from here —
 * those remain the Shopify app's job — so there is no duplication risk.
 *
 * Consent: sends are gated on Shopify Customer Privacy
 * (marketing consent AND CCPA sale-of-data opt-out), and the gate FAILS
 * CLOSED — if the consent API is absent or not yet loaded, tracking is
 * treated as NOT allowed, consistent with nh-consent-banner.js and
 * nh-consent-default-deny.liquid (NH-CCPA-013 / gap ref B17, 2026-08-04).
 * Events blocked are queued in sessionStorage and flushed when the
 * storefront dispatches `visitorConsentCollected` or once the consent API
 * appears with consent already recorded, so a 5th-page event that precedes
 * a late cookie-banner acceptance is not lost across navigation.
 *
 * Configured from snippets/pinterest-intent-relay.liquid:
 *   data-tag-id  — Pinterest tag ID (required)
 *   data-events  — comma-separated allowlist of NH event names to relay
 *                  (default: "session_5_pages")
 */

const PENDING_KEY = 'nh_pintrk_pending'; // consent-blocked events awaiting flush
const SENT_KEY = 'nh_pintrk_sent'; // event names already relayed this session

function safeGet(storage, key) {
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(storage, key, value) {
  try {
    storage.setItem(key, value);
  } catch (_) {
    // Private browsing / quota exceeded — fail silently
  }
}

function getList(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (_) {
    return [];
  }
}

function setList(key, list) {
  safeSet(sessionStorage, key, JSON.stringify(list));
}

// Sending browse events to an ad platform requires BOTH signals:
//   - marketingAllowed()  — GDPR-style opt-in marketing consent (EU)
//   - saleOfDataAllowed() — CCPA/CPRA "sale or sharing" opt-out, which also
//     reflects Global Privacy Control headers and the storefront's
//     "Your Privacy Choices" opt-out page (US/California)
function trackingAllowed() {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (!privacy) {
    // Fail CLOSED: if the consent API has not loaded (yet), assume no consent,
    // matching nh-consent-banner.js and nh-consent-default-deny.liquid.
    // Blocked events are queued in sessionStorage and flushed once consent is
    // confirmed, so nothing is lost — only deferred.
    return false;
  }
  const marketing = typeof privacy.marketingAllowed === 'function' ? privacy.marketingAllowed() : false;
  const saleOfData = typeof privacy.saleOfDataAllowed === 'function' ? privacy.saleOfDataAllowed() : false;
  return marketing && saleOfData;
}

// The consent API is injected asynchronously by the storefront. Poll briefly
// for it (same cadence as nh-consent-banner.js) so that a visitor whose
// consent was ALREADY recorded on a previous page still gets queued events
// flushed on this page — `visitorConsentCollected` only fires on a NEW choice.
const PRIVACY_API_POLL_MS = 250;
const PRIVACY_API_POLL_MAX = 40;

function whenPrivacyApiReady(callback) {
  if (window.Shopify && window.Shopify.customerPrivacy) {
    callback();
    return;
  }
  let tries = 0;
  const timer = setInterval(() => {
    tries += 1;
    if (window.Shopify && window.Shopify.customerPrivacy) {
      clearInterval(timer);
      callback();
    } else if (tries >= PRIVACY_API_POLL_MAX) {
      clearInterval(timer);
      // API never appeared — stay failed-closed; events remain queued.
    }
  }, PRIVACY_API_POLL_MS);
}

class PinterestIntentRelay extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.tagId = this.dataset.tagId;
    if (!this.tagId) return;

    const events = this.dataset.events || 'session_5_pages';
    this.allowlist = events.split(',').map((name) => name.trim()).filter(Boolean);
    this.seen = new WeakSet();

    this.handleIntentEvent = (customEvent) => this.processDetail(customEvent.detail);
    window.addEventListener('nh:intent-event', this.handleIntentEvent);

    this.handleConsent = () => this.flushPending();
    document.addEventListener('visitorConsentCollected', this.handleConsent);

    // Catch up on events published before this element connected
    (window.__nhIntentEventBuffer || []).forEach((detail) => this.processDetail(detail));

    // Flush any consent-blocked events carried over from earlier pages,
    // waiting for the consent API if it has not been injected yet.
    whenPrivacyApiReady(() => {
      if (trackingAllowed()) this.flushPending();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('nh:intent-event', this.handleIntentEvent);
    document.removeEventListener('visitorConsentCollected', this.handleConsent);
  }

  processDetail(detail) {
    if (!detail || this.seen.has(detail)) return;
    this.seen.add(detail);

    if (!this.allowlist.includes(detail.event)) return;
    if (getList(SENT_KEY).includes(detail.event)) return;

    if (trackingAllowed()) {
      this.send(detail.event, detail.payload);
    } else {
      const pending = getList(PENDING_KEY);
      if (!pending.some((entry) => entry.event === detail.event)) {
        pending.push({ event: detail.event, payload: detail.payload });
        setList(PENDING_KEY, pending);
      }
    }
  }

  flushPending() {
    if (!trackingAllowed()) return;
    const pending = getList(PENDING_KEY);
    if (!pending.length) return;
    setList(PENDING_KEY, []);
    pending.forEach((entry) => {
      if (!getList(SENT_KEY).includes(entry.event)) {
        this.send(entry.event, entry.payload);
      }
    });
  }

  send(eventName, payload) {
    this.ensurePintrk();
    window.pintrk('track', eventName, {
      ...payload,
      event_id: `${eventName}-${safeGet(sessionStorage, 'nh_hiv_session_id') || Date.now()}`,
    });
    const sent = getList(SENT_KEY);
    sent.push(eventName);
    setList(SENT_KEY, sent);
  }

  // Standard Pinterest bootstrap, minus `pintrk('page')` — page telemetry
  // and all standard events stay with the Shopify app's sandboxed pixel.
  ensurePintrk() {
    if (this.loaded) return;
    this.loaded = true;
    if (!window.pintrk) {
      window.pintrk = function () {
        window.pintrk.queue.push(Array.prototype.slice.call(arguments));
      };
      const stub = window.pintrk;
      stub.queue = [];
      stub.version = '3.0';
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://s.pinimg.com/ct/core.js';
      document.head.appendChild(script);
    }
    window.pintrk('load', this.tagId);
  }
}

if (!customElements.get('pinterest-intent-relay')) {
  customElements.define('pinterest-intent-relay', PinterestIntentRelay);
}
